import { assert } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { PreferredSizeRequestEvent } from '@sas-dvr/ltjs-commons';
import { Observable, of, from } from 'rxjs';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { fromPortableEvent, withRetained } from './rxUtils';
import { mergeMap, tap, ignoreElements } from 'rxjs/operators';
import {
  VisualElementController,
  DataStateChangeEvent,
  PositionedModelController,
} from '@sas-dvr/ltjs-bird';
import { TitleState } from '@sas-dvr/internal-va-react-core/store/title/TitleState';
import { StateObservable } from 'redux-observable';
import { AnyAction } from 'redux';

import cn from '@sas-dvr/internal-va-react-core/components/Title/Title.css.json';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { getOrCreateLayoutConstraint } from './layoutUtils';
import { getRootClassName } from '../../config';
import { getScrollbarSize } from '@sas-dvr/internal-va-react-core/utils/scrollbarSize';
import { StringDict } from '@sas-dvr/internal-va-react-core/utility-types';

export function parseHTMLString(htmlText: string | null): string {
  if (!htmlText) {
    return '';
  }

  // collect only the contents of the body tag.
  // the style tag containing the base css clobbers app css
  const result = htmlText.match(/<body>(.*)<\/body>/s);

  if (!result?.[1]) {
    assert(false, 'Encountered an unexpected content when parsing html string: ' + htmlText);
    return htmlText;
  }

  return result[1];
}

// FIXME: text measuring needs to use the correct font:
//      | - Notosans for certain locales and high-contrast
//      | - some components are supposed to use Helvetica?
// NOTE: this is copied from Pane.scss and should match it's fallback
const outerDiv = document.createElement('div');
outerDiv.style.fontFamily = "'AvenirNext', Arial, Helvetica, Verdana, 'Open Sans', sans-serif";
outerDiv.style.visibility = 'hidden';
outerDiv.style.position = 'absolute';
outerDiv.style.lineHeight = 'normal';

const div = document.createElement('div');
outerDiv.appendChild(div);

export function measureTextHeight(
  html: string,
  width: number,
  className?: string,
  style: StringDict<string> = {}
) {
  div.innerHTML = html;
  outerDiv.style.width = width + 'px';
  outerDiv.className = getRootClassName() || '';
  div.className = className || '';
  for (const [k, v] of Object.entries(style)) {
    div.style.setProperty(k, v ?? '');
  }

  document.body.appendChild(outerDiv);
  const height = outerDiv.getBoundingClientRect().height;
  document.body.removeChild(outerDiv);
  return height;
}

export function getTitleObservables(
  id: string,
  positionedController: PositionedModelController | null,
  visualElementController: VisualElementController | null,
  state$: StateObservable<ReportState>
) {
  const observables: Observable<AnyAction>[] = [];
  if (!positionedController || !visualElementController) {
    return observables;
  }
  const layoutProxy = positionedController.getLayoutProxy();
  if (layoutProxy) {
    // HEIGHT_REQUEST gets fired a lot, so cache
    const { measureHeight } = createRichTextHeightCache();

    // FIXME: HEIGHT_REQUEST is not fired for elements in precision containers, and
    //        if we use PREFERRED_HEIGHT_REQUEST footerHeight is ignored
    observables.push(
      fromPortableEvent<PreferredSizeRequestEvent>(
        layoutProxy,
        PreferredSizeRequestEvent.HEIGHT_REQUEST
      ).pipe(
        withRetained(layoutProxy),
        tap((e) => {
          const layoutData = state$.value.ui.birdLayout.byId[id];
          const title = visualElementController.getTitleAsHTML();
          const width = e.getConstrainedSize();
          const titlePadding = layoutData?.titlePadding;
          let height = measureHeight(title, width, cn.title, {
            '--title-font-size': '1.125rem', // dt.font.size.xxl
            '--title-custom-padding':
              typeof titlePadding === 'number' ? titlePadding + 'px' : titlePadding,
          });

          if (layoutData) {
            if (layoutData.constraints) {
              height += layoutData.constraints.additionalContentHeight || 0;
            }
            if (layoutData.aboutInfoLayout) {
              height += layoutData.aboutInfoLayout.height || 0;
            }
            if (layoutData.breadcrumbLayout) {
              height += layoutData.breadcrumbLayout.height || 0;
            }
            if (layoutData.scrollbars && layoutData.scrollbars.horizontal) {
              height += getScrollbarSize();
            }
          }

          const layoutConstraint = getOrCreateLayoutConstraint(layoutProxy);
          layoutConstraint.setFooterHeight(height);
        }),
        ignoreElements()
      )
    );
  }
  observables.push(
    fromPortableEvent(visualElementController, DataStateChangeEvent.DATACHANGE).pipe(
      mergeMap(() => {
        const title = visualElementController.getTitleAsHTML();
        return from([
          TitleState.actions.set(id, parseHTMLString(title)),
          BirdLayoutState.actions.notifyInvalidateLayout(),
        ]);
      })
    )
  );
  const title = parseHTMLString(visualElementController.getTitleAsHTML());
  observables.push(of(TitleState.actions.set(id, title)));
  return observables;
}

export function createRichTextHeightCache() {
  let cacheText: string | null = null;
  const cache: Map<number, number> = new Map();
  return {
    measureHeight(
      text: string | null,
      width: number,
      className?: string,
      styles?: StringDict<string>
    ): number {
      let height: number | undefined = undefined;
      if (cacheText !== text) {
        cache.clear();
        cacheText = text;
      }
      if (!text) {
        height = 0;
      } else {
        height = cache.get(width);
        if (height === undefined) {
          height = measureTextHeight(parseHTMLString(text), width, className, styles);
          cache.set(width, height);
        }
      }
      return height;
    },
  };
}
